import { CURRENT_CLIENT } from '@/constants'
import { useTenantLogoSrc } from '@/hooks/use-tenant-logo-src'

/**
 * Fill for masked dark-on-light logo assets.
 * Use `foreground` (not `primary-foreground`) so dark guest/app themes stay legible —
 * TalentCo’s `--primary-foreground` is navy on dark and disappears on the sticky bar.
 */
export const TENANT_LOGO_MASK_FILL_CLASS = 'bg-foreground'

export function TenantLogoImg({
  className,
  style,
  logoOnLightBackground,
  alt,
  /** Parent supplies an accessible name (e.g. wrapping `Link aria-label="Home"`). */
  decorative = false,
}: {
  className?: string
  style?: import('react').CSSProperties
  logoOnLightBackground?: boolean
  alt?: string
  decorative?: boolean
}) {
  const cc = CURRENT_CLIENT as {
    logoPath?: string
    logoPathLight?: string
    logoPathDark?: string
    name?: string
  }
  const src = useTenantLogoSrc({
    logoPath: cc.logoPath,
    logoPathLight: cc.logoPathLight,
    logoPathDark: cc.logoPathDark,
  })

  const label = decorative ? '' : alt ?? cc.name ?? 'Brand'

  if (!src) return null

  if (logoOnLightBackground) {
    return (
      <span
        className={className ?? `inline-block h-7 aspect-[123/27] ${TENANT_LOGO_MASK_FILL_CLASS}`}
        style={{
          ...style,
          maskImage: `url(${src})`,
          WebkitMaskImage: `url(${src})`,
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
        }}
        {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
      />
    )
  }

  return (
    <img
      style={style}
      src={src}
      alt={decorative ? '' : label}
      className={className ?? 'h-7 w-auto'}
      {...(decorative ? { 'aria-hidden': true } : {})}
    />
  )
}
